import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { error, failedResponse, getBackendUrl, INVALID_ID, loaded, loading, LoadStateInfo, notStarted } from "../common";
import { PresenceEnum, PresenceInfo } from "./model";
import { getToken } from "../../features/user/UserAvatar";

export interface PresenceChangeState {
    requests: {
        state: LoadStateInfo;
        request: PresenceChangeRequest
    }[];
}
const initialState: PresenceChangeState = {
    requests: [],
};
export function findPresenceChangeRequest(requests: PresenceChangeState["requests"], payload: PresenceChangeRequest) {
    return requests.find((r) =>
        r.request.subjectPerformedId == payload.subjectPerformedId &&
        r.request.studentId == payload.studentId
    );
}
export interface PresenceChangeRequest {
    subjectPerformedId: number;
    studentId: string;
    present: PresenceEnum;
}
export const presenceChange = createAsyncThunk(
    "presenceChange",
    async (param: PresenceChangeRequest, thunkAPI) => {
        console.debug(`change presence for subjectPerformed ${param.subjectPerformedId}, student ${param.studentId} to status ${param.present}`);

        const response = await fetch(`${getBackendUrl()}/api/presence/change`, {
            method: "POST",
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(param)
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
);

export const presenceChangeSlice = createSlice({
    name: "presenceChange",
    initialState,
    reducers: {
        clear: (state, action: PayloadAction<PresenceChangeRequest>) => {
            const request = findPresenceChangeRequest(state.requests, action.payload);
            if (request) {
                request.state = notStarted();
                request.request = action.payload;
            } else if (!request) {
                state.requests.push({
                    state: notStarted(),
                    request: action.payload
                });
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(presenceChange.pending, (state, action) => {
                const payload = action.meta.arg;
                const request = findPresenceChangeRequest(state.requests, payload);
                if (request) {
                    request.state = loading();
                    request.request = payload;
                } else if (!request) {
                    state.requests.push({
                        state: loading(),
                        request: payload
                    });
                }
            })
            .addCase(presenceChange.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                const payload = action.meta.arg;
                const request = findPresenceChangeRequest(state.requests, payload);
                if (request) {
                    request.state = loaded();
                    request.request = payload;
                }
            })
            .addCase(presenceChange.rejected, (state, action) => {
                const payload = action.meta.arg;
                const request = findPresenceChangeRequest(state.requests, payload);
                if (request) {
                    request.state = error(action);
                    request.request = payload;
                }
            });
    },
});

export const selectPresenceInsUpd = (state: RootState) => state.presenceChange;

export default presenceChangeSlice.reducer;
